import type { Mode, Politician } from './types';

/** Day #1 of the game. */
export const EPOCH = '2026-10-04';
const TIME_ZONE = 'Europe/Bratislava';
const DAY_MS = 86_400_000;

const dateFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const timeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Calendar date in Slovakia, as YYYY-MM-DD. */
export function dateKey(now: Date = new Date()): string {
  return dateFmt.format(now);
}

function toUtcDays(key: string): number {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / DAY_MS;
}

/** 1-based puzzle number for a date key. */
export function dayNumber(key: string): number {
  return toUtcDays(key) - toUtcDays(EPOCH) + 1;
}

export function shiftDateKey(key: string, days: number): string {
  return new Date((toUtcDays(key) + days) * DAY_MS).toISOString().slice(0, 10);
}

/** Milliseconds until midnight in Slovakia. */
export function msUntilNextDay(now: Date = new Date()): number {
  const [h, m, s] = timeFmt.format(now).split(':').map(Number);
  return DAY_MS - ((h * 60 + m) * 60 + s) * 1000 - now.getMilliseconds();
}

function hash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Answers served before the schedule below existed (the roster grew on day 3).
 * Kept so finished games and "Včerajší politik" never change.
 */
const PINNED: Record<Mode, string[]> = {
  classic: ['kotleba', 'krajniak', 'duris-nicholsonova'],
  photo: ['csaky', 'fico', 'sulik'],
};

interface Deck {
  pool: Politician[];
  sorted: Politician[];
  byId: Map<string, Politician>;
  days: Record<Mode, string[]>;
  used: Record<Mode, Set<string>>;
}

let deck: Deck | null = null;

/**
 * Deals answers like cards: each day takes a random person nobody has had since
 * the deck was last reshuffled, from those already added by that day. People
 * join on their `added` date, so adding politicians never changes earlier days.
 */
function deal(d: Deck, mode: Mode, day: number, eligible: Politician[], avoid: string | null): string {
  const used = d.used[mode];
  const pinned = PINNED[mode][day - 1];
  if (pinned && d.byId.has(pinned)) {
    used.add(pinned);
    return pinned;
  }
  let open = eligible.filter((p) => !used.has(p.id));
  if (!open.length) {
    used.clear();
    open = eligible;
  }
  const options = open.filter((p) => p.id !== avoid);
  const from = options.length ? options : open.length ? open : d.sorted;
  const id = from[Math.floor(mulberry32(hash(`politikdle:${mode}:${day}`))() * from.length)].id;
  used.add(id);
  return id;
}

export function dailyAnswer(pool: Politician[], mode: Mode, day: number): Politician {
  if (deck?.pool !== pool) {
    const sorted = pool.slice().sort((a, b) => a.id.localeCompare(b.id));
    deck = {
      pool,
      sorted,
      byId: new Map(sorted.map((p) => [p.id, p])),
      days: { classic: [], photo: [] },
      used: { classic: new Set(), photo: new Set() },
    };
  }
  const d = deck;
  const target = Math.max(1, day);
  for (let n = d.days.classic.length + 1; n <= target; n++) {
    const date = shiftDateKey(EPOCH, n - 1);
    const eligible = d.sorted.filter((p) => (p.added ?? EPOCH) <= date);
    const classic = deal(d, 'classic', n, eligible, d.days.classic.at(-1) ?? null);
    // Never the same person in both modes on one day.
    const photo = deal(d, 'photo', n, eligible.filter((p) => p.image), classic);
    d.days.classic.push(classic);
    d.days.photo.push(photo);
  }
  return d.byId.get(d.days[mode][target - 1])!;
}

export function randomAnswer(pool: Politician[], exclude: string[]): Politician {
  const options = pool.filter((p) => !exclude.includes(p.id));
  const from = options.length ? options : pool;
  return from[Math.floor(Math.random() * from.length)];
}
